// DocumentSetTabs.tsx
// Tabs for switching Personal / Shared sets with inline search toggle.
import React, { useEffect, useRef, useState } from 'react';
import { Tabs, Input, Button } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
    fetchDocumentsBySet,
    setCurrentSet,
    setSearch,
} from '../../redux/slices/documentSetSlice';
import { SetType } from '../../services/document-set-service';

const TAB_ITEMS = [
    { key: 'shared',   label: 'Общие' },
    { key: 'personal', label: 'Личные' },
];

const DocumentSetTabs: React.FC = () => {
    const dispatch = useAppDispatch();
    const currentSet = useAppSelector((s) => s.documentSet.currentSet);
    const ownerId = useAppSelector((s) => s.documentSet.ownerId);
    const searchValue = useAppSelector((s) => s.documentSet.search);

    const [searchOpen, setSearchOpen] = useState(false);
    const inputRef = useRef<any>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        dispatch(fetchDocumentsBySet({ setType: currentSet, ownerId: ownerId ?? undefined }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (searchOpen) inputRef.current?.focus();
    }, [searchOpen]);

    const handleTabChange = (key: string) => {
        const setType = key as SetType;
        dispatch(setCurrentSet(setType));
        dispatch(fetchDocumentsBySet({ setType, ownerId: ownerId ?? undefined, search: searchValue || undefined }));
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        dispatch(setSearch(value));
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            dispatch(fetchDocumentsBySet({
                setType: currentSet,
                ownerId: ownerId ?? undefined,
                search: value || undefined,
            }));
        }, 400);
    };

    const handleSearchClose = () => {
        setSearchOpen(false);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        dispatch(setSearch(''));
        dispatch(fetchDocumentsBySet({ setType: currentSet, ownerId: ownerId ?? undefined }));
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tabs
                activeKey={currentSet}
                items={TAB_ITEMS}
                onChange={handleTabChange}
                style={{ marginBottom: 0, flex: 1 }}
            />
            {!searchOpen ? (
                <Button
                    type="text"
                    icon={<SearchOutlined />}
                    onClick={() => setSearchOpen(true)}
                    style={{ marginBottom: 4 }}
                />
            ) : (
                <Input
                    ref={inputRef}
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder="Поиск по названию..."
                    style={{ width: 220, marginBottom: 4 }}
                    suffix={
                        <CloseOutlined
                            style={{ cursor: 'pointer', color: '#aaa' }}
                            onClick={handleSearchClose}
                        />
                    }
                />
            )}
        </div>
    );
};

export default DocumentSetTabs;
